import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import TopNavigation from './TopNavigation'
import Footer from './Footer'

const TermsOfUse = () => {
  const [isSecretRoute, setIsSecretRoute] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const hostname = window.location.hostname
    const pathname = window.location.pathname
    const isSecretSubdomain = hostname.startsWith('secret.') || hostname === 'secret.localhost'
    const isSecretPath = pathname.startsWith('/secret')
    setIsSecretRoute(isSecretSubdomain || isSecretPath)
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-background">
      <TopNavigation backTo="/" backText="Back to Home" />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-foreground mb-8">Terms of Use</h1>
          <p className="text-muted-foreground mb-8">
            <strong>Last updated:</strong> September, 2025
          </p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing and using the Tailtations website or participating in our SECRET program, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Tailtations LLC provides gourmet deep-fried lobster tail services including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Online ordering and delivery services</li>
                <li>Event catering and popup experiences</li>
                <li>SECRET membership program with exclusive access</li>
                <li>Customer support and account management</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. SECRET Program Terms</h2>
              
              <h3 className="text-xl font-medium text-foreground mb-3">3.1 Membership</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Membership is free but requires account registration</li>
                <li>Members must be 18 years or older</li>
                <li>One membership per person; duplicate accounts may be terminated</li>
                <li>Membership benefits are subject to availability and may change</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">3.2 Exclusive Access</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Advance notice of popup events and special offerings</li>
                <li>Priority access to limited-quantity items</li>
                <li>Exclusive member-only events and experiences</li>
                <li>Special pricing and promotional offers</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">3.3 Transfer and Sharing</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Members may transfer credits or access to other members</li>
                <li>All transfers must be conducted through official channels</li>
                <li>Transferred items are subject to the same terms and conditions</li>
                <li>We reserve the right to limit or restrict transfers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. Orders and Payment</h2>
              
              <h3 className="text-xl font-medium text-foreground mb-3">4.1 Order Processing</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>All orders are subject to availability and acceptance</li>
                <li>We reserve the right to refuse or cancel any order</li>
                <li>Order confirmations will be sent via email or SMS</li>
                <li>Delivery times are estimates and not guaranteed</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">4.2 Payment Terms</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Payment is required at the time of order placement</li>
                <li>We accept major credit cards and digital payment methods</li>
                <li>All prices are subject to applicable taxes and fees</li>
                <li>Refunds are processed according to our refund policy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. User Responsibilities</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You agree to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Use the service only for lawful purposes</li>
                <li>Respect other users and our staff</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Not attempt to circumvent security measures</li>
                <li>Not share or transfer your account to others</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. Prohibited Activities</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You may not:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Use the service for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the service</li>
                <li>Create multiple accounts to circumvent restrictions</li>
                <li>Resell or redistribute our products without permission</li>
                <li>Use automated systems to access the service</li>
                <li>Harass, abuse, or harm other users or staff</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                All content, trademarks, logos, and intellectual property on our website and in our services are owned by Tailtations LLC or our licensors. You may not use, reproduce, or distribute any content without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                To the maximum extent permitted by law, Tailtations LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or relating to your use of our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. Disclaimers</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our services are provided "as is" without warranties of any kind, either express or implied. We do not warrant that our services will be uninterrupted, error-free, or free of viruses or other harmful components.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">10. Termination</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may terminate or suspend your account and access to our services at any time, with or without notice, for any reason, including violation of these terms. Upon termination, your right to use the service will cease immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">11. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These terms shall be governed by and construed in accordance with the laws of the State of Wyoming, without regard to its conflict of law provisions. Any disputes arising from these terms shall be resolved in the courts of Wyoming.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">12. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these terms at any time. We will notify users of any material changes by posting the updated terms on our website. Your continued use of our services after such changes constitutes acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">13. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about these Terms of Use, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-foreground font-medium">Tailtations LLC</p>
                <p className="text-muted-foreground">Email: legal@tailtations.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      {!isSecretRoute && <Footer />}
    </div>
  )
}

export default TermsOfUse
